<template>
  <section class="driver-form-section">
    <header class="driver-form-section__header">
      <p class="driver-form-section__title">
        {{ $t('driverProfile.personalInfo') }}
      </p>
    </header>

    <div class="driver-form-panel">
      <div class="driver-form-panel__main">
        <div class="driver-form-panel__photo">
          <UploadImage
            v-model="model.photo"
            ref="uploadimage"
            style="height: 132px;"
          />
        </div>

        <div class="driver-form-panel__body">
          <div class="driver-form-grid driver-form-grid--triple">
            <div class="driver-form-field">
              <label class="driver-form-label required">
                {{ $t('driverProfile.driverName') }}
              </label>
              <input
                v-model="model.driver_name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.has('driver_name') }"
                :placeholder="$t('driverProfile.driverName')"
              />
              <div class="invalid-feedback" v-if="errors.has('driver_name')">
                {{ errors.first('driver_name') }}
              </div>
            </div>

            <div class="driver-form-field">
              <label class="driver-form-label required">
                {{ $t('driverProfile.dateOfBirth') }}
              </label>
              <a-date-picker
                v-model="model.dob"
                class="tw-w-full"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                :placeholder="$t('driverProfile.dateOfBirth')"
              />
              <div class="driver-form-error" v-if="errors.has('dob')">
                {{ errors.first('dob') }}
              </div>
            </div>

            <div class="driver-form-field">
              <label class="driver-form-label required">
                {{ $t('driverProfile.sex') }}
              </label>
              <div class="driver-form-radio-row">
                <a-radio-group v-model="model.sex" name="sex">
                  <a-radio value="M">
                    {{ $t('driverProfile.male') }}
                  </a-radio>
                  <a-radio value="F">
                    {{ $t('driverProfile.female') }}
                  </a-radio>
                </a-radio-group>
              </div>
              <div class="driver-form-error" v-if="errors.has('sex')">
                {{ errors.first('sex') }}
              </div>
            </div>
          </div>

          <div class="driver-form-grid driver-form-grid--triple">
            <div class="driver-form-field">
              <label class="driver-form-label required">
                {{ $t('driverProfile.phone1') }}
              </label>
              <input
                v-model="model.phone1"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.has('phone1') }"
                :placeholder="$t('driverProfile.phone1')"
              />
              <div class="invalid-feedback" v-if="errors.has('phone1')">
                {{ errors.first('phone1') }}
              </div>
            </div>

            <div class="driver-form-field">
              <label class="driver-form-label">
                {{ $t('driverProfile.phone2') }}
              </label>
              <input
                v-model="model.phone2"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.has('phone2') }"
                :placeholder="$t('driverProfile.phone2')"
              />
              <div class="invalid-feedback" v-if="errors.has('phone2')">
                {{ errors.first('phone2') }}
              </div>
            </div>

            <div class="driver-form-field">
              <label class="driver-form-label">
                {{ $t('driverProfile.phone3') }}
              </label>
              <input
                v-model="model.phone3"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.has('phone3') }"
                :placeholder="$t('driverProfile.phone3')"
              />
              <div class="invalid-feedback" v-if="errors.has('phone3')">
                {{ errors.first('phone3') }}
              </div>
            </div>
          </div>

          <div class="driver-form-grid driver-form-grid--triple">
            <div class="driver-form-field">
              <label class="driver-form-label">
                {{ $t('driverProfile.agency') }}
              </label>
              <a-select
                v-model="model.agency_id"
                class="tw-w-full"
                :options="agencyList"
                showSearch
                :filterOption="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                :placeholder="$t('driverProfile.agency')"
                :disabled="!$whois.admin() && agencyList.length <= 1"
                allowClear
              />
              <div class="driver-form-error" v-if="errors.has('agency_id')">
                {{ errors.first('agency_id') }}
              </div>
            </div>

            <div class="driver-form-field">
              <label class="driver-form-label">
                {{ $t('driverProfile.telChatId') }}
              </label>
              <input
                v-model="model.tel_chat_id"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.has('tel_chat_id') }"
                :placeholder="$t('driverProfile.telChatId')"
              />
              <div class="invalid-feedback" v-if="errors.has('tel_chat_id')">
                {{ errors.first('tel_chat_id') }}
              </div>
            </div>

            <div class="driver-form-field">
              <label class="driver-form-label">Status</label>
              <div class="driver-form-status">
                <ts-checkbox v-model="model.is_internal_driver">
                  {{ $t('driverProfile.internalDriver') }}
                </ts-checkbox>
                <ts-checkbox v-model="model.is_active">
                  {{ $t('driverProfile.active') }}
                </ts-checkbox>
              </div>
            </div>
          </div>

          <div class="driver-form-grid driver-form-grid--double">
            <div class="driver-form-field">
              <label class="driver-form-label">
                {{ $t('driverProfile.emergencyContact') }}
              </label>
              <input
                v-model="model.emergency_contact"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.has('emergency_contact') }"
                :placeholder="$t('driverProfile.emergencyContact')"
              />
              <div
                class="invalid-feedback"
                v-if="errors.has('emergency_contact')"
              >
                {{ errors.first('emergency_contact') }}
              </div>
            </div>

            <div class="driver-form-field">
              <label class="driver-form-label">
                {{ $t('driverProfile.emergencyRelation') }}
              </label>
              <input
                v-model="model.emergency_relationship"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.has('emergency_relationship') }"
                :placeholder="$t('driverProfile.emergencyRelation')"
              />
              <div
                class="invalid-feedback"
                v-if="errors.has('emergency_relationship')"
              >
                {{ errors.first('emergency_relationship') }}
              </div>
            </div>
          </div>

          <div class="driver-form-grid driver-form-grid--double">
            <div class="driver-form-field">
              <label class="driver-form-label required">
                {{ $t('driverProfile.contractStartDate') }}
              </label>
              <a-date-picker
                v-model="model.contract_start_date"
                class="tw-w-full"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                :placeholder="$t('driverProfile.contractStartDate')"
              />
              <div
                class="driver-form-error"
                v-if="errors.has('contract_start_date')"
              >
                {{ errors.first('contract_start_date') }}
              </div>
            </div>

            <div class="driver-form-field">
              <label class="driver-form-label required">
                {{ $t('driverProfile.contractEndDate') }}
              </label>
              <a-date-picker
                v-model="model.contract_end_date"
                class="tw-w-full"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                :placeholder="$t('driverProfile.contractEndDate')"
              />
              <div
                class="driver-form-error"
                v-if="errors.has('contract_end_date')"
              >
                {{ errors.first('contract_end_date') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { Errors } from 'form-backend-validation';
import { mapState } from 'vuex';
import UploadImage from './upload-image.vue';

export default {
  name: 'personalInfo',
  props: ['value', 'validate'],
  components: {
    UploadImage,
  },
  data() {
    return {
      errors: new Errors(),
      model: {}
    };
  },
  created() {
    this.model = this.value;
  },
  computed: {
    ...mapState('driver/driverProfile', ['formModels']),
    agencyList() {
      if (!this.formModels.agency) return [];

      return this.formModels.agency.map(el => ({
        value: el.agency_id,
        label: el.agency_name
      }));
    }
  },
  watch: {
    agencyList: {
      handler(options) {
        if (!this.model.agency_id && options.length === 1) {
          this.model.agency_id = options[0].value;
        }
      },
      immediate: true
    },
    validate: function(error) {
      this.errors = new Errors(error.errors);
    }
  }
};
</script>

<style scoped>
.driver-form-section__header {
  margin-bottom: 10px;
}

.driver-form-section__title {
  margin: 0;
  color: #8ea0b4;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.driver-form-panel {
  border: 1px solid #dfe6ee;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
}

.driver-form-panel__main {
  display: grid;
  grid-template-columns: 108px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

.driver-form-panel__photo {
  min-width: 0;
}

.driver-form-panel__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.driver-form-grid {
  display: grid;
  gap: 10px 16px;
}

.driver-form-grid--triple {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.driver-form-grid--double {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.driver-form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.driver-form-label {
  margin: 0;
  color: #3f4d5d;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.driver-form-error {
  color: #dc2626;
  font-size: 12px;
}

.driver-form-radio-row {
  min-height: 32px;
  display: flex;
  align-items: center;
}

.driver-form-status {
  min-height: 32px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 2px;
}

@media (max-width: 991px) {
  .driver-form-panel__main {
    grid-template-columns: 1fr;
  }

  .driver-form-panel__photo {
    max-width: 108px;
  }

  .driver-form-grid--triple,
  .driver-form-grid--double {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .driver-form-panel {
    padding: 12px;
  }

  .driver-form-grid--triple,
  .driver-form-grid--double {
    grid-template-columns: 1fr;
  }
}
</style>
